import React from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DecimalRatingStars } from './DecimalRatingStars';
import { useAppLocale } from '../../providers/LocaleProvider';

interface LearnerRatingCommentsTriggerProps {
  onOpenComments: () => void;
  loading?: boolean;
  average?: number | null;
  ratingLabel?: string | null;
  ratingCount?: number;
  /** Tailwind hover color token prefix, e.g. `blue` or `sky`. */
  accent?: 'blue' | 'sky';
  /** Light text for use on a photo hero background. */
  onHeroImage?: boolean;
}

const accentClasses = {
  blue: {
    group: 'hover:text-blue-500 focus-visible:ring-blue-400/80 dark:group-hover:text-blue-400',
    score: 'group-hover:text-blue-500 dark:group-hover:text-blue-400',
    icon: 'group-hover:text-blue-500 dark:group-hover:text-blue-400',
    empty: 'text-gray-500 dark:text-gray-400'
  },
  sky: {
    group: 'hover:text-sky-600 focus-visible:ring-sky-400/80 dark:group-hover:text-sky-400',
    score: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
    icon: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
    empty: 'text-gray-500 dark:text-gray-400'
  }
} as const;

/**
 * Compact rating row: only stars, numeric score, and comment icon open the modal —
 * not the surrounding header row.
 */
export const LearnerRatingCommentsTrigger: React.FC<LearnerRatingCommentsTriggerProps> = ({
  onOpenComments,
  loading = false,
  average = null,
  ratingLabel = null,
  ratingCount = 0,
  accent = 'blue',
  onHeroImage = false
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const colors = onHeroImage
    ? {
        group: 'text-white/90 hover:text-white focus-visible:ring-white/70',
        score: 'text-white group-hover:text-white',
        icon: 'text-white/85 group-hover:text-white',
        empty: 'text-white/75'
      }
    : accentClasses[accent];
  const hasRating = ratingLabel != null && average != null;
  const commentTitle = t('comments.viewCommentsAndRatings');
  const commentAriaLabel =
    hasRating && ratingCount > 0
      ? t('comments.viewCommentsAndRatingsDetail', {
          rating: ratingLabel,
          count: ratingCount,
        })
      : commentTitle;

  const openBtnClass = `group inline-flex items-center gap-1 rounded-md border-0 bg-transparent p-0 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 disabled:pointer-events-none ${colors.group}`;

  const commentButton = (
    <button
      type="button"
      onClick={onOpenComments}
      title={commentTitle}
      aria-label={commentAriaLabel}
      disabled={loading && average == null}
      className={`${openBtnClass} shrink-0 disabled:pointer-events-none`}
    >
      <MessageSquare
        className={`h-4 w-4 shrink-0 text-gray-400 ${colors.icon} dark:text-gray-500`}
        aria-hidden
      />
    </button>
  );

  const ratingBody =
    loading && average == null ? (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] leading-none text-amber-700/80 dark:text-amber-300/80"
        role="status"
        aria-live="polite"
        aria-label={t('comments.loadingRating')}
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        <span className="sr-only">{t('comments.loadingRating')}</span>
      </span>
    ) : hasRating ? (
      <button
        type="button"
        onClick={onOpenComments}
        title={commentTitle}
        aria-label={commentAriaLabel}
        className={`${openBtnClass} ${isRtl ? 'flex-row-reverse' : ''}`}
      >
        <DecimalRatingStars average={average} size="sm" />
        <span
          className={`text-[11px] font-bold tabular-nums leading-none text-gray-500 ${colors.score}`}
        >
          {ratingLabel}
        </span>
      </button>
    ) : (
      <button
        type="button"
        onClick={onOpenComments}
        title={commentTitle}
        aria-label={commentAriaLabel}
        className={`${openBtnClass} truncate text-[11px] leading-tight ${colors.empty}`}
      >
        {t('comments.noLearnerRatingsYet')}
      </button>
    );

  return (
    <div
      className={`flex min-w-0 flex-1 items-center gap-1.5 ${isRtl ? 'justify-end' : ''}`}
      dir="ltr"
    >
      {isRtl ? (
        <>
          {commentButton}
          {ratingBody}
        </>
      ) : (
        <>
          {ratingBody}
          {commentButton}
        </>
      )}
    </div>
  );
};
